import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { networkManager } from '../services/NetworkManager';
import { DataBuild } from '../types';
import { useNotification } from '../contexts/NotificationContext';
import { Differ, Viewer } from 'json-diff-kit';
import 'json-diff-kit/dist/viewer.css';

interface BundleComparison {
  bundleName: string;
  gamedataA: any;
  gamedataB: any;
  status: 'match' | 'mismatch' | 'only-in-a' | 'only-in-b' | 'unknown';
}

interface BundleDetail {
  bundleName: string;
  existsInA: boolean;
  existsInB: boolean;
  bundleA?: any;
  bundleB?: any;
}

const DataComparisonPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showNotification } = useNotification();
  
  // States for gamedata selection
  const [gamedataList, setGamedataList] = useState<DataBuild[]>([]);
  const [selectedGamedataA, setSelectedGamedataA] = useState<DataBuild | null>(null);
  const [selectedGamedataB, setSelectedGamedataB] = useState<DataBuild | null>(null);
  
  // States for dropdowns
  const [showDropdownA, setShowDropdownA] = useState(false);
  const [showDropdownB, setShowDropdownB] = useState(false);
  const [searchTermA, setSearchTermA] = useState('');
  const [searchTermB, setSearchTermB] = useState('');
  
  // States for comparison
  const [comparison, setComparison] = useState<BundleComparison[]>([]);
  const [bundleDetails, setBundleDetails] = useState<BundleDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingGamedata, setLoadingGamedata] = useState(true);
  
  // States for JSON comparison
  const [showComparison, setShowComparison] = useState(false);
  const [comparingBundle, setComparingBundle] = useState<string>('');
  const [diffResult, setDiffResult] = useState<any>(null);
  const [loadingComparison, setLoadingComparison] = useState(false);
  
  // Initialize the differ
  const differ = new Differ({
    detectCircular: true,
    maxDepth: Infinity,
    showModifications: true,
    arrayDiffMethod: 'lcs',
  });
  
  // Refs for dropdowns
  const dropdownARef = useRef<HTMLDivElement>(null);
  const dropdownBRef = useRef<HTMLDivElement>(null);

  // Load gamedata list and check URL parameters
  useEffect(() => {
    loadGamedataList();
  }, []);

  // Check URL parameters for pre-selected gamedata
  useEffect(() => {
    const gamedataAId = searchParams.get('gamedataA');
    const gamedataBId = searchParams.get('gamedataB');
    
    if (gamedataList.length > 0) {
      if (gamedataAId) {
        const gamedataA = gamedataList.find(g => g._id === gamedataAId);
        if (gamedataA) {
          setSelectedGamedataA(gamedataA);
          setSearchTermA(gamedataA.title);
        }
      }
      
      if (gamedataBId) {
        const gamedataB = gamedataList.find(g => g._id === gamedataBId);
        if (gamedataB) {
          setSelectedGamedataB(gamedataB);
          setSearchTermB(gamedataB.title);
        }
      }
    }
  }, [gamedataList, searchParams]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownARef.current && !dropdownARef.current.contains(event.target as Node)) {
        setShowDropdownA(false);
      }
      if (dropdownBRef.current && !dropdownBRef.current.contains(event.target as Node)) {
        setShowDropdownB(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadGamedataList = async () => {
    try {
      setLoadingGamedata(true);
      const data = await networkManager.getDataBuilds();
      // Filter only successful builds
      const successfulBuilds = Array.isArray(data) 
        ? data.filter(build => build.status === 'success')
        : [];
      setGamedataList(successfulBuilds);
    } catch (err) {
      console.error('Error loading gamedata:', err);
      showNotification('Failed to load gamedata list', 'error', 5000);
      setGamedataList([]);
    } finally {
      setLoadingGamedata(false);
    }
  };

  // Filter functions
  const filteredGamedataA = (gamedataList || []).filter(gamedata =>
    gamedata.title.toLowerCase().includes(searchTermA.toLowerCase())
  );

  const filteredGamedataB = (gamedataList || []).filter(gamedata =>
    gamedata.title.toLowerCase().includes(searchTermB.toLowerCase())
  );

  // Handle gamedata selection
  const handleGamedataASelect = (gamedata: DataBuild) => {
    setSelectedGamedataA(gamedata);
    setSearchTermA(gamedata.title);
    setShowDropdownA(false);
    
    // Update URL with selected gamedata
    const newParams = new URLSearchParams(searchParams);
    newParams.set('gamedataA', gamedata._id);
    setSearchParams(newParams);
  };

  const handleGamedataBSelect = (gamedata: DataBuild) => {
    setSelectedGamedataB(gamedata);
    setSearchTermB(gamedata.title);
    setShowDropdownB(false);
    
    // Update URL with selected gamedata
    const newParams = new URLSearchParams(searchParams);
    newParams.set('gamedataB', gamedata._id);
    setSearchParams(newParams);
  };

  // Handle input changes
  const handleInputAChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTermA(value);
    setShowDropdownA(true);
    if (!value) {
      setSelectedGamedataA(null);
    }
  };

  const handleInputBChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTermB(value);
    setShowDropdownB(true);
    if (!value) {
      setSelectedGamedataB(null);
    }
  };

  // Handle input focus
  const handleInputAFocus = () => setShowDropdownA(true);
  const handleInputBFocus = () => setShowDropdownB(true);

  // Clear selections
  const clearSelectionA = () => {
    setSelectedGamedataA(null);
    setSearchTermA('');
    setShowDropdownA(false);
    
    // Remove from URL
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('gamedataA');
    setSearchParams(newParams);
  };

  const clearSelectionB = () => {
    setSelectedGamedataB(null);
    setSearchTermB('');
    setShowDropdownB(false);
    
    // Remove from URL
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('gamedataB');
    setSearchParams(newParams);
  };

  // Compare gamedata
  const compareGamedata = async () => {
    if (!selectedGamedataA || !selectedGamedataB) {
      showNotification('Please select both gamedata items to compare', 'warning', 5000);
      return;
    }

    if (selectedGamedataA._id === selectedGamedataB._id) {
      showNotification('Please select different gamedata items to compare', 'warning', 5000);
      return;
    }

    try {
      setLoading(true);
      
      // Get detailed information for both gamedata
      const [gamedataADetails, gamedataBDetails] = await Promise.all([
        networkManager.getDataBuild(selectedGamedataA._id),
        networkManager.getDataBuild(selectedGamedataB._id)
      ]);

      // Extract bundles from both gamedata
      const bundlesA = gamedataADetails.bundles || {};
      const bundlesB = gamedataBDetails.bundles || {};

      // Get all unique bundle names
      const allBundleNames = new Set([
        ...Object.keys(bundlesA),
        ...Object.keys(bundlesB)
      ]);

      // Create bundle details for comparison
      const bundleDetailsResult: BundleDetail[] = Array.from(allBundleNames).map(bundleName => {
        const existsInA = bundleName in bundlesA;
        const existsInB = bundleName in bundlesB;
        
        return {
          bundleName,
          existsInA,
          existsInB,
          bundleA: existsInA ? bundlesA[bundleName] : undefined,
          bundleB: existsInB ? bundlesB[bundleName] : undefined
        };
      });

      setBundleDetails(bundleDetailsResult);
      
    } catch (err) {
      console.error('Error comparing gamedata:', err);
      showNotification('Failed to compare gamedata', 'error', 5000);
    } finally {
      setLoading(false);
    }
  };

  // Compare specific bundle
  const compareBundle = async (bundleName: string) => {
    if (!selectedGamedataA || !selectedGamedataB) {
      showNotification('Please select both gamedata items', 'warning', 5000);
      return;
    }

    try {
      setLoadingComparison(true);
      setComparingBundle(bundleName);
      
      // Get decoded bundle data from both gamedata
      const [jsonDataA, jsonDataB] = await Promise.all([
        networkManager.decodeBundle(selectedGamedataA._id, bundleName),
        networkManager.decodeBundle(selectedGamedataB._id, bundleName)
      ]);

      // Remove null values to prevent comparison failures
      const cleanJsonA = removeNullValues(jsonDataA);
      const cleanJsonB = removeNullValues(jsonDataB);

      // Generate diff using json-diff-kit
      const diff = differ.diff(cleanJsonA, cleanJsonB);
      setDiffResult(diff);
      setShowComparison(true);
      
    } catch (err) {
      console.error('Error comparing bundle:', err);
      showNotification('Failed to compare bundle', 'error', 5000);
    } finally {
      setLoadingComparison(false);
    }
  };

  // Remove null values from JSON to prevent comparison failures
  const removeNullValues = (obj: any): any => {
    if (obj === null || obj === undefined) {
      return undefined;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => removeNullValues(item)).filter(item => item !== undefined);
    }
    
    if (typeof obj === 'object') {
      const cleaned: any = {};
      for (const [key, value] of Object.entries(obj)) {
        const cleanedValue = removeNullValues(value);
        if (cleanedValue !== undefined) {
          cleaned[key] = cleanedValue;
        }
      }
      return cleaned;
    }
    
    return obj;
  };

  // Close comparison view
  const closeComparison = () => {
    setShowComparison(false);
    setComparingBundle('');
    setDiffResult(null);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gamedata Comparison</h1>
          <p className="text-gray-600 mt-1">Compare data bundles between two gamedata items</p>
        </div>
        <div className="flex items-center space-x-3">
          {(selectedGamedataA || selectedGamedataB) && (
            <button
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                showNotification('URL copied to clipboard!', 'success', 3000);
              }}
              className="btn btn-secondary"
              title="Copy shareable URL"
            >
              <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy URL
            </button>
          )}
          <button
            onClick={() => navigate('/data-builds')}
            className="btn btn-secondary"
          >
            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Builds
          </button>
        </div>
      </div>

      {/* Selection Section */}
      <div className="card p-8">
        <div className="flex items-center mb-6">
          <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
            <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Select Gamedata to Compare</h2>
            <p className="text-sm text-gray-600">Choose two gamedata items to compare their data bundles</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Gamedata A Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Gamedata A
            </label>
            <div className="relative" ref={dropdownARef}>
              <input
                type="text"
                value={searchTermA}
                onChange={handleInputAChange}
                onFocus={handleInputAFocus}
                className="input text-base w-full"
                placeholder={loadingGamedata ? "Loading..." : "Search for gamedata..."}
                disabled={loadingGamedata}
                autoComplete="off"
              />
              
              {selectedGamedataA && (
                <button
                  type="button"
                  onClick={clearSelectionA}
                  className="absolute right-10 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              
              {showDropdownA && !loadingGamedata && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredGamedataA.length > 0 ? (
                    filteredGamedataA.map((gamedata) => (
                      <div
                        key={gamedata._id}
                        onClick={() => handleGamedataASelect(gamedata)}
                        className="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                      >
                        <div className="font-medium text-gray-900 truncate" title={gamedata.title}>{gamedata.title}</div>
                        <div className="text-sm text-gray-600 truncate" title={gamedata.description}>{gamedata.description}</div>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-gray-500 text-sm">
                      No gamedata found
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Gamedata B Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Gamedata B
            </label>
            <div className="relative" ref={dropdownBRef}>
              <input
                type="text"
                value={searchTermB}
                onChange={handleInputBChange}
                onFocus={handleInputBFocus}
                className="input text-base w-full"
                placeholder={loadingGamedata ? "Loading..." : "Search for gamedata..."}
                disabled={loadingGamedata}
                autoComplete="off"
              />
              
              {selectedGamedataB && (
                <button
                  type="button"
                  onClick={clearSelectionB}
                  className="absolute right-10 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              
              {showDropdownB && !loadingGamedata && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredGamedataB.length > 0 ? (
                    filteredGamedataB.map((gamedata) => (
                      <div
                        key={gamedata._id}
                        onClick={() => handleGamedataBSelect(gamedata)}
                        className="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                      >
                        <div className="font-medium text-gray-900 truncate" title={gamedata.title}>{gamedata.title}</div>
                        <div className="text-sm text-gray-600 truncate" title={gamedata.description}>{gamedata.description}</div>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-gray-500 text-sm">
                      No gamedata found
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Compare Button */}
        <div className="flex justify-center">
          <button
            onClick={compareGamedata}
            disabled={loading || !selectedGamedataA || !selectedGamedataB}
            className="btn btn-primary px-8 py-3"
          >
            {loading ? (
              <div className="flex items-center">
                <div className="spinner h-5 w-5 mr-2"></div>
                Generating Bundle List...
              </div>
            ) : (
              <div className="flex items-center">
                <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Generate Bundle List
              </div>
            )}
          </button>
        </div>
      </div>

      {/* Comparison Results with Tabs */}
      {bundleDetails.length > 0 && (
        <div className="card p-8">
          {/* Tab Navigation */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-6">
              <button
                onClick={() => setShowComparison(false)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  !showComparison 
                    ? 'bg-blue-100 text-blue-700 border-2 border-blue-200' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <span>Bundle List ({bundleDetails.length})</span>
              </button>
              
              {showComparison && (
                <button
                  onClick={() => setShowComparison(true)}
                  className="flex items-center space-x-2 px-4 py-2 rounded-lg font-medium bg-green-100 text-green-700 border-2 border-green-200"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <span>Comparing: {comparingBundle}</span>
                </button>
              )}
            </div>
            
            {showComparison && (
              <button
                onClick={closeComparison}
                className="btn btn-secondary"
              >
                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Close Comparison
              </button>
            )}
          </div>

          {/* Tab Content */}
          {!showComparison ? (
            // Bundle List Tab
            <div>
              <div className="flex items-center mb-6">
                <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                  <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Available Bundles</h2>
                  <p className="text-sm text-gray-600">
                    Select a bundle to compare between {selectedGamedataA?.title} and {selectedGamedataB?.title}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {bundleDetails.map((bundle, index) => {
                  const canCompare = bundle.existsInA && bundle.existsInB;
                  return (
                    <div
                      key={index}
                      className={`border rounded-lg p-4 transition-all duration-200 hover:shadow-md ${
                        canCompare 
                          ? 'border-blue-200 bg-blue-50 hover:bg-blue-100' 
                          : 'border-yellow-200 bg-yellow-50 hover:bg-yellow-100'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900 mb-1">{bundle.bundleName}</h3>
                          <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                            canCompare 
                              ? 'bg-blue-200 text-blue-800' 
                              : 'bg-yellow-200 text-yellow-800'
                          }`}>
                            {canCompare ? 'Comparable' : 'Not Comparable'}
                          </span>
                        </div>
                      </div>
                      
                      <div className="text-sm text-gray-600 mb-3">
                        {canCompare 
                          ? 'Both gamedata contain this bundle'
                          : !bundle.existsInA 
                            ? `Only in ${selectedGamedataB?.title}`
                            : `Only in ${selectedGamedataA?.title}`
                        }
                      </div>
                      
                      {canCompare && (
                        <button
                          onClick={() => compareBundle(bundle.bundleName)}
                          disabled={loadingComparison && comparingBundle === bundle.bundleName}
                          className="w-full btn btn-primary py-2 text-sm"
                        >
                          {loadingComparison && comparingBundle === bundle.bundleName ? (
                            <div className="flex items-center justify-center">
                              <div className="spinner h-4 w-4 mr-2"></div>
                              Loading...
                            </div>
                          ) : (
                            <div className="flex items-center justify-center">
                              <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                              </svg>
                              Compare Bundle
                            </div>
                          )}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            // Comparison Tab
            <div>
              <div className="flex items-center mb-6">
                <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                  <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Bundle Comparison: {comparingBundle}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {selectedGamedataA?.title} vs {selectedGamedataB?.title}
                  </p>
                </div>
              </div>
              
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <Viewer
                  diff={diffResult}
                  indent={2}
                  lineNumbers={true}
                  highlightInlineDiff={true}
                  hideUnchangedLines={true}
                  hideUnchangedNodes={true}
                  inlineDiffOptions={{
                    mode: 'word',
                    wordSeparator: ' ',
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DataComparisonPage;
